import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { productDebitAccountFilters } from './product-debit-account.filters';
import {
  PRODUCT_DEBIT_ACCOUNT_MAX,
  PRODUCT_DEBIT_ACCOUNT_PAGE_SIZE,
  PRODUCT_DEBIT_ACCOUNT_PATH,
} from './product-debit-account.search';

/** router.tsx:1728-1734: `strictPermission` (hasScopedPermission); BankAccount homônima não vale. */
export const MANAGE_AUTOMATIC_DEBIT = can('CreditProduct', 'ManageAutomaticDebit', {
  strict: true,
});

/**
 * Gate da rota-mãe `/cadastro` do legado (router.tsx:1576-1594): `permission: { resource: [5], type:
 * 'Read' }`, sem `strictPermission`, logo `hasPermission` com o array (userContext.tsx:321-336). O
 * `RoutePermissionGuard` (RoutePermissionGuard.tsx:14-50) percorre pai e filho, nessa ordem. É a
 * mesma lista da seção `register` em `app/shell/navigation.ts`. Correção local (revisão !11101, R2)
 * até a fundação expressar o gate da seção no manifesto.
 */
export const REGISTER_SECTION_READ = [
  can('CreditProduct', 'Read'),
  can('BankAccountProduct', 'Read'),
  can('Fund', 'Read'),
  can('CreditProductRules', 'Read'),
  can('BillingProduct', 'Read'),
] as const;

export type ProductDebitAccountDenial = 'section' | 'root' | 'permission';

export interface ProductDebitAccountAccessEnv {
  readonly can: (resource: string, type: string, options?: { strict?: boolean }) => boolean;
  readonly rootTenancy: boolean;
}

/** Na ordem do guard do legado: pai `/cadastro` (Read em um dos 5), depois root, depois a ação estrita. */
export function productDebitAccountDenial(
  env: ProductDebitAccountAccessEnv,
): ProductDebitAccountDenial | null {
  // `hasPermission(array, 'Read')` do legado é um "ou" sobre os recursos: equivale ao `some`.
  if (!REGISTER_SECTION_READ.some((entry) => env.can(entry.resource, entry.action)))
    return 'section';
  if (!env.rootTenancy) return 'root';
  const { resource, action: type } = MANAGE_AUTOMATIC_DEBIT;
  if (!env.can(resource, type, { strict: true })) return 'permission';
  return null;
}

const needsScreen = () => {
  throw new Error('This action requires the open automatic debit products screen.');
};

export const productDebitAccountFilter = filterAction(
  'productDebitAccount.filter',
  productDebitAccountFilters,
  {
    description: msg`Filtra a lista de produtos de crédito usada para escolher os produtos: searchString (busca livre), categoryName (categoria), amortizationType (tipo de amortização), name (nome do produto) e termStatus (status de vigência). Filtro omitido é limpo.`,
  },
);

export const productDebitAccountPage = listPageAction('productDebitAccount.products.page', {
  defaultSize: PRODUCT_DEBIT_ACCOUNT_PAGE_SIZE,
});

export const registerInput = z.strictObject({
  cosifBankAccountCode: z.string().min(1),
  productIds: z
    .array(z.string().min(1))
    .min(1)
    .max(PRODUCT_DEBIT_ACCOUNT_MAX)
    .refine((ids) => new Set(ids).size === ids.length),
});
export type RegisterInput = z.infer<typeof registerInput>;

/** Quantos produtos a prévia do agente nomeia antes do "e mais n" (legível num diálogo). */
export const PREVIEW_PRODUCT_LIMIT = 10;

/** Lista da prévia: os primeiros nomes (ou ids) e, se passar do limite, "e mais n" (R3 !11101). */
export function previewProductList(labels: readonly string[]): string {
  const shown = labels.slice(0, PREVIEW_PRODUCT_LIMIT).join(', ');
  const rest = labels.length - PREVIEW_PRODUCT_LIMIT;
  return rest > 0 ? i18n._(msg`${shown} e mais ${rest}`) : shown;
}

export const productDebitAccountRegister = action<RegisterInput>({
  id: 'productDebitAccount.register',
  kind: 'write',
  label: msg`Cadastrar`,
  description: msg`Liga até 100 produtos de crédito a uma conta Cosif com conta de débito vinculada, para a liquidação automática. Pede a confirmação da pessoa; o resultado lista cada produto que não foi configurado.`,
  permission: MANAGE_AUTOMATIC_DEBIT,
  input: registerInput,
  preview: (input) => ({
    title: i18n._(msg`Cadastrar produtos em liquidação automática`),
    detail: i18n._(
      msg`${input.productIds.length} produto(s) na conta Cosif ${input.cosifBankAccountCode}: ${previewProductList(input.productIds)}.`,
    ),
  }),
  run: needsScreen,
});

export const productDebitAccountScreen = defineScreen({
  id: 'productDebitAccount.batch',
  path: PRODUCT_DEBIT_ACCOUNT_PATH,
  title: msg`Produtos em Liquidação Automática`,
  description: msg`Escolha uma conta Cosif com conta de débito e até 100 produtos de crédito, e cadastre todos de uma vez na liquidação automática.`,
  permission: MANAGE_AUTOMATIC_DEBIT,
  access: { rootTenancy: true },
  place: { icon: 'wallet', section: 'register' },
  actions: [productDebitAccountFilter, productDebitAccountPage, productDebitAccountRegister],
});
